import {
  IMPERIAL_DISTANCE_COUNTRIES,
  kmToMiles,
} from "@redotech/redo-fulfillment-common/distance-conversion";
import { ShipmentMethodName } from "@redotech/redo-return-core-common/draft-return/draft-return-enums";
import type { ShipmentMethodOption } from "../../shipment-method/util";

/** In the shopper's distance unit: miles for imperial countries, km elsewhere. */
const MAX_NEARBY_DISTANCE = 10;

function getMaxNearbyDistanceInMiles(
  customerCountry: string | undefined,
): number {
  return IMPERIAL_DISTANCE_COUNTRIES.has(customerCountry ?? "")
    ? MAX_NEARBY_DISTANCE
    : kmToMiles(MAX_NEARBY_DISTANCE);
}

interface LocationWithDistance {
  distance?: string | null;
  distanceMiles?: number;
  outsideConfiguredRadius?: boolean | null;
}

export function areAllLocationsOutsideConfiguredRadius(
  locations:
    | readonly { outsideConfiguredRadius?: boolean | null }[]
    | null
    | undefined,
): boolean {
  return (
    !!locations &&
    locations.length > 0 &&
    locations.every((location) => location.outsideConfiguredRadius === true)
  );
}

export function getDistanceInMiles(
  distance: string | null | undefined,
): number | undefined {
  if (!distance) {
    return undefined;
  }
  const miles = parseFloat(distance);
  return Number.isFinite(miles) && miles >= 0 ? miles : undefined;
}

export function sortLocationsByDistance<T extends LocationWithDistance>(
  locations: T[],
): T[] {
  return [...locations].sort(
    (a, b) =>
      (getLocationDistanceInMiles(a) ?? Infinity) -
      (getLocationDistanceInMiles(b) ?? Infinity),
  );
}

function getLocationDistanceInMiles(
  location: LocationWithDistance,
): number | undefined {
  const distanceMiles = location.distanceMiles;
  if (
    distanceMiles !== undefined &&
    Number.isFinite(distanceMiles) &&
    distanceMiles >= 0
  ) {
    return distanceMiles;
  }
  return getDistanceInMiles(location.distance);
}

function getLocations(option: ShipmentMethodOption): LocationWithDistance[] {
  switch (option.type) {
    case ShipmentMethodName.DROPOFF:
      return option.dropoffs ?? [];
    case ShipmentMethodName.BLUE_YONDER:
      return option.blueYonderDetails?.locations ?? [];
    case ShipmentMethodName.IN_STORE:
      return option.inStoreLocations ?? [];
    case ShipmentMethodName.PICKUP:
    case ShipmentMethodName.SETTLEMENT:
    case ShipmentMethodName.SELF_SHIP:
    case ShipmentMethodName.HAPPY_RETURNS:
    case ShipmentMethodName.RETURN_BEAR:
    case ShipmentMethodName.MANUAL_LABEL:
      return [];
  }
}

export function hasNearbyDropoff(
  options: ShipmentMethodOption[],
  customerCountry: string | undefined,
): boolean {
  const maxNearbyDistanceMiles = getMaxNearbyDistanceInMiles(customerCountry);
  return options.flatMap(getLocationsForNearbyCheck).some((location) => {
    const distance = getLocationDistanceInMiles(location);
    return distance !== undefined && distance < maxNearbyDistanceMiles;
  });
}

function getLocationsForNearbyCheck(
  option: ShipmentMethodOption,
): LocationWithDistance[] {
  const locations = getLocations(option);
  if (option.type !== ShipmentMethodName.IN_STORE) {
    return locations;
  }
  return locations.filter((location) => !location.outsideConfiguredRadius);
}

function isDefaultDropoffCandidate(option: ShipmentMethodOption): boolean {
  if (
    option.type === ShipmentMethodName.IN_STORE &&
    areAllLocationsOutsideConfiguredRadius(option.inStoreLocations)
  ) {
    return false;
  }
  return (
    option.type === ShipmentMethodName.DROPOFF ||
    option.type === ShipmentMethodName.BLUE_YONDER ||
    option.type === ShipmentMethodName.IN_STORE
  );
}

export function getDefaultShipmentOption<T extends ShipmentMethodOption>(
  options: T[],
  hasNearby: boolean,
): T | undefined {
  const firstDropoff = options.find(isDefaultDropoffCandidate);
  const selfShip = options.find(
    (option) => option.type === ShipmentMethodName.SELF_SHIP,
  );
  if (!firstDropoff) {
    const hasInStoreOutsideRadius = options.some(
      (option) =>
        option.type === ShipmentMethodName.IN_STORE &&
        areAllLocationsOutsideConfiguredRadius(option.inStoreLocations),
    );
    return hasInStoreOutsideRadius ? selfShip : undefined;
  }
  if (hasNearby) {
    return firstDropoff;
  }

  return selfShip ?? firstDropoff;
}
